import React, {useEffect, useRef, useState, useCallback} from "react";
import {LineChart} from "@mui/x-charts";
import {ChartWrapper} from "components/atoms/Dashboard/Dashboard.styles";
import {IoIosArrowDown} from "react-icons/io";
import Select from "components/molecules/Select";
import {useTheme} from "styled-components";
import {useSelector} from "react-redux";
import analyticsService from "services/analyticsService";
import {useAuthContext} from "Context/authContext";

const RevenueLineChart = ({selectedPeriod, customDateRange, currentStoreId}) => {
    const userState = useSelector(state => state.business.user);
    const { user } = useAuthContext();
    const finalUser = user || userState;
    const business = useSelector(state => state.business.businessId);
    const businessIdFromAuth = finalUser?.businessId;
    const businessId = businessIdFromAuth || business;
    const [revenueData, setRevenueData] = useState([]);
    const [loading, setLoading] = useState(false);
    const theme = useTheme();
    const color = theme?.light?.primary || "var(--primary)";

    const getRevenueData = useCallback(async () => {
        try {
            setLoading(true);
            // Simplified businessId logic
            const restaurantId = businessId || (Array.isArray(finalUser?.businessId) ? finalUser.businessId[0] : null);
            
            if (!restaurantId) {
                console.warn('📊 Revenue: No restaurant ID available');
                setLoading(false);
                return;
            }
            
            const params = {
                restaurantId,
            };
            
            // Add storeId if available
            if (currentStoreId) {
                params.storeId = currentStoreId;
            }
            
            // Add time period filters
            if (selectedPeriod) {
                params.period = selectedPeriod;
            }
            
            // Add custom date range if selected
            if (selectedPeriod === 'custom') {
                if (customDateRange?.startDate) {
                    params.startDate = customDateRange.startDate;
                }
                if (customDateRange?.endDate) {
                    params.endDate = customDateRange.endDate;
                }
            }
            
            const res = await analyticsService.getRevenue(params);
            console.log('📊 Revenue: API response:', res);
            
            if (res?.success && res?.data) {
                setRevenueData(res.data.monthlyRevenue || []);
            }
        } catch (error) {
            console.error('📊 Revenue: Error fetching revenue data:', error);
            // Keep default empty array on error
            setRevenueData([]);
        } finally {
            setLoading(false);
        }
    }, [businessId, finalUser, currentStoreId, selectedPeriod, customDateRange]);

    useEffect(() => {
        if (currentStoreId && (businessId || finalUser?.businessId?.[0])) {
            getRevenueData();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [currentStoreId, selectedPeriod, customDateRange]);

    // Extract chart data from API response
    const xLabels = revenueData.map(item => item.month) || [];
    const pData = revenueData.map(item => item.revenue) || [];
    
    // Calculate Y-axis range
    const maxRevenue = Math.max(...pData, 0);
    const yAxisMax = Math.ceil(maxRevenue * 1.1); // Add 10% padding to top

    return (
        <ChartWrapper>
            <div className="heading">
                <div className="title">
                    <h2>Revenue</h2>
                    <p>Revenue of the selected period</p>
                </div>
                {/* <div className="revenue-dropdown" ref={headerDropdownRef}>
                    <div className="dropdown" onClick={() => setIsDropdownOpen(!isDropdownOpen)}>
                        <p>{selectedOption}</p>
                        <IoIosArrowDown style={{color: "grey"}} />
                    </div>
                    {isDropdownOpen && (
                        <ul className="styled-select">
                            {dropdownOptions.map(option => (
                                <li key={option} onClick={() => handleOptionClick(option)}>
                                    {option}
                                </li>
                            ))}
                        </ul>
                    )}
                </div> */}
                {/* <div className="revenueDropdown">
          <Select
            placeholder="Monthly"
            options={revenueData}
            suffix={<IoIosArrowDown size={20} />}
            noMargin
          />
        </div> */}
            </div>
            <div className="lineChart">
                {loading ? (
                    <div style={{
                        display: 'flex', 
                        justifyContent: 'center', 
                        alignItems: 'center', 
                        height: '300px',
                        color: '#999'
                    }}>
                        Loading revenue data...
                    </div>
                ) : xLabels.length === 0 || pData.length === 0 ? (
                    <div style={{
                        display: 'flex', 
                        justifyContent: 'center', 
                        alignItems: 'center', 
                        height: '300px',
                        color: '#999'
                    }}>
                        No revenue data available for the selected period
                    </div>
                ) : (
                    <LineChart
                        xAxis={[{
                            scaleType: "point", 
                            data: xLabels,
                            tickLabelStyle: {
                                fontSize: 12,
                                fill: '#666',
                            }
                        }]}
                        yAxis={[{
                            min: 0,
                            max: yAxisMax || undefined,
                            tickLabelStyle: {
                                fontSize: 12,
                                fill: '#666',
                            },
                            valueFormatter: (value) => `PKR ${value.toLocaleString('en-US', {
                                minimumFractionDigits: 0,
                                maximumFractionDigits: 0
                            })}`,
                        }]}
                        series={[
                            {
                                data: pData,
                                area: true,
                                color: color,
                                showMark: true,
                                curve: "monotoneX",
                                valueFormatter: (value) => value !== null ? `PKR ${value.toLocaleString('en-US', {
                                    minimumFractionDigits: 2,
                                    maximumFractionDigits: 2
                                })}` : 'No data',
                            },
                        ]}
                        height={300}
                        margin={{left: 70, right: 30, top: 30, bottom: 30}}
                        grid={{vertical: true, horizontal: true}}
                        tooltip={{
                            trigger: 'axis',
                        }}
                        slotProps={{
                            legend: { hidden: true }
                        }}
                    />
                )}
            </div>
        </ChartWrapper>
    );
};

export default RevenueLineChart;
